import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import {
  Button,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NumberInput,
  Skeleton,
  Switch,
} from '@autone/ui-next/atoms';
import { usePaginatedSelectionContext } from '@autone/ui-next/components';
import { useCallback, useMemo, useState } from 'react';

import {
  managedByAutoneToBoolean,
  matchSystemRecommendationToBoolean,
} from '../../../../../utils/quick-filters';
import { type APP_NAME } from '../../../../consts';
import { useBulkEditAssortmentSkuLocationsMutation } from '../../../../redux/assortment';
import { useAssortmentSessionStorageAtom } from '../../../../storage/session-storage';
import { useGetSkuLocationsTableQueryArgs } from '../hooks/useGetSkuLocationsTableQueryArgs';
import { useGetSkuLocationsTableSelectionCounts } from '../hooks/useGetSkuLocationsTableSelectionCounts';
import { parseRowId } from '../utilities/row-id';

import { DoubleConfirmationInput } from './DoubleConfirmationInput';

interface SkuLocationsTableInitialAllocationModalContentProps {
  onEditComplete?: (result: 'ok' | 'error') => void;
}

export const SkuLocationsTableInitialAllocationModalContent = ({
  onEditComplete,
}: SkuLocationsTableInitialAllocationModalContentProps) => {
  const {
    body: { filters, advanced_filter },
  } = useGetSkuLocationsTableQueryArgs({ searchTerm: '' });
  const [{ matchSystemRecommendationToggle, managedByAutoneToggle }] =
    useAssortmentSessionStorageAtom('skuLocationsTable.extrasMenu');

  const [value, setValue] = useState<number | null>(null);
  const [assort, setAssort] = useState(false);
  const [confirmationText, setConfirmationText] = useState<number | null>(null);
  const { t, common } = useAutoneTranslation<typeof APP_NAME>();
  const [state] = usePaginatedSelectionContext();

  const { data, isFetching } = useGetSkuLocationsTableSelectionCounts();
  const { counts } = data ?? {};

  const confirmationOk = useMemo(
    () => confirmationText === state.selectedRowCount,
    [confirmationText, state.selectedRowCount],
  );

  const [bulkEditSkuLocations, { isLoading }] =
    useBulkEditAssortmentSkuLocationsMutation();

  const handleBulkEditSkuLocations = useCallback(async () => {
    const isMangedByAutone = managedByAutoneToBoolean(managedByAutoneToggle);
    const matchesSystemRecommendation = matchSystemRecommendationToBoolean(
      matchSystemRecommendationToggle,
    );
    if (value === null) return;

    const result = await (async () => {
      if (state.mode === 'bulk') {
        return await bulkEditSkuLocations({
          body: {
            selection: {
              selection_type: 'criteria',
              filters,
              advanced_filter,
              sku_locations_to_exclude: state.excludeList.map((id) => {
                const { skuId, locationId } = parseRowId(id);
                return { sku_id: skuId, location_id: locationId };
              }),
              is_managed_by_user:
                isMangedByAutone === null ? null : !isMangedByAutone,
              matches_system_recommendation: matchesSystemRecommendation,
            },
            assortment_update: {
              update_type: 'initial_allocation',
              new_value: value,
              set_is_assorted: assort,
            },
          },
        });
      } else {
        return await bulkEditSkuLocations({
          body: {
            selection: {
              selection_type: 'sku_location_list',
              sku_locations: state.includeList.map((id) => {
                const { skuId, locationId } = parseRowId(id);
                return { sku_id: skuId, location_id: locationId };
              }),
            },
            assortment_update: {
              update_type: 'initial_allocation',
              new_value: value,
              set_is_assorted: assort,
            },
          },
        });
      }
    })();

    if ('error' in result) {
      onEditComplete?.('error');
    } else {
      trackAnalyticsEvent({
        category: 'assortment_tables',
        action: 'sku_locations_table:bulk_set_ia_updated',
        properties: {
          num_sku_locations: state.selectedRowCount,
          new_ia: value,
        },
      });
      onEditComplete?.('ok');
    }
  }, [
    bulkEditSkuLocations,
    onEditComplete,
    managedByAutoneToggle,
    matchSystemRecommendationToggle,
    filters,
    advanced_filter,
    state,
    value,
    assort,
  ]);

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('bulk-actions.set-ia.title')}</DialogTitle>
      </DialogHeader>
      <DialogDescription className="flex flex-col gap-4">
        <p>{t('bulk-actions.set-ia.description')}</p>
        {isFetching ? (
          <Skeleton className="h-4 w-full mt-2" />
        ) : (
          <p className="font-medium">
            {t('bulk-actions.set-ia.ia-x-sku-locs', {
              count: state.selectedRowCount,
              products: common.withValue('products', {
                value: counts?.products,
                count: counts?.products,
              }),
              locations: common.withValue('locations', {
                value: counts?.locations,
                count: counts?.locations,
              }),
            })}
          </p>
        )}
        <div className="w-fit">
          <NumberInput
            id={'set-initial-allocation-input'}
            aria-label={t('action-bar.set-ia')}
            placeholder={t('action-bar.set-ia')}
            min={0}
            max={100_000_000}
            value={value}
            onChange={(v) => setValue(v)}
          />
        </div>
        <div className="flex flex-row gap-3 items-center">
          <Switch
            name={'assort-items-switch'}
            checked={assort}
            onCheckedChange={(v) => setAssort(!!v)}
          />
          <label htmlFor="assort-items-switch">
            {t('bulk-actions.set-ia.assort-selected-items')}
          </label>
        </div>
      </DialogDescription>
      <DoubleConfirmationInput
        id="set-ia-double-confirmation-input"
        selectedRowCount={state.selectedRowCount}
        value={confirmationText}
        onChange={(value) => setConfirmationText(value)}
      />
      <DialogFooter className="w-full flex flex-row justify-between">
        <DialogClose asChild>
          <Button
            id={`cancel-autome-assortment-button`}
            aria-label={common('buttons.cancel')}
            variant={'outline'}
          >
            {common('buttons.cancel')}
          </Button>
        </DialogClose>
        <Button
          id={`set-ia-button`}
          loading={isLoading}
          disabled={value === null || !confirmationOk}
          aria-label={t('action-bar.set-ia')}
          variant={'default'}
          onClick={handleBulkEditSkuLocations}
        >
          {assort ? t('action-bar.set-ia-and-assort') : t('action-bar.set-ia')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
